import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../features/auth/useAuth';
import { withNext } from '../lib/nextPath';
import { useOnboardingStatus } from '../features/onboarding/useOnboardingStatus';
import { FullPageStatus } from './FullPageStatus';

interface ProtectedRouteProps {
  /**
   * Gate the route on completed onboarding as well as auth. Knowledge base and
   * generation screens stay locked until onboarding is done (SRS §2).
   */
  requireOnboarding?: boolean;
}

/** Redirects to /onboarding until `profiles.onboarding_completed` is true. */
function OnboardingGate() {
  const { status } = useOnboardingStatus();

  if (status === 'loading') {
    return <FullPageStatus message="Loading your workspace" />;
  }

  if (status === 'incomplete') {
    return <Navigate to="/onboarding" replace />;
  }

  // 'complete' passes. 'unknown' also passes: the check could not run (see
  // useOnboardingStatus) and a failed lookup must not lock a signed-in user out.
  return <Outlet />;
}

/** Gates a route subtree on an active Supabase session. */
export function ProtectedRoute({ requireOnboarding = false }: ProtectedRouteProps) {
  const { session, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <FullPageStatus message="Checking your session" />;
  }

  if (!session) {
    // In the query as well as the state, so the path survives a detour to
    // sign-up and the confirm email (an invite link opened signed out).
    const from = `${location.pathname}${location.search}`;
    return <Navigate to={withNext('/login', from)} replace state={{ from }} />;
  }

  return requireOnboarding ? <OnboardingGate /> : <Outlet />;
}
